import { MediumFormatting } from "@faire/web/common/localization/formatting";
import { StrictLocalMsg } from "@faire/web/ui/lib/localization";
import React from "react";

import { useFirstOrderIncentiveFormattedData } from "@faire/retailer/hooks/firstOrderIncentive/useFirstOrderIncentiveFormattedData";
import { useFirstOrderIncentiveStore } from "@faire/retailer/hooks/firstOrderIncentive/useFirstOrderIncentiveStore";
import { useFirstOrderIncentiveTimeRemaining } from "@faire/retailer/hooks/firstOrderIncentive/useFirstOrderIncentiveTimeRemaining";

export const useAutoExtendText = () => {
  const {
    formattedIncentiveData,
    isMaximalistFoi,
    isFirstOrderForFaireDirectIncentive,
  } = useFirstOrderIncentiveStore();
  const { expiresAt } = formattedIncentiveData;

  const { daysRemaining: daysRemainingNumber, hoursRemaining } =
    useFirstOrderIncentiveTimeRemaining(expiresAt ?? 0);

  const { formattedIncentivePercent } = useFirstOrderIncentiveFormattedData();

  const autoExtendLessThan24HoursText = () => {
    if (isFirstOrderForFaireDirectIncentive) {
      return (
        <StrictLocalMsg
          defaultMessage="{hoursRemainingNumber, plural, one{<MediumFormatting># hour left</MediumFormatting> to get {percentNumber} off your first order with any brand.} other{<MediumFormatting># hours left</MediumFormatting> to get {percentNumber} off your first order with any brand.}}"
          description={{
            text: "Emphasize that there are only few days left for our promotion",
          }}
          values={{
            MediumFormatting,
            // Round up to 1 so we don't show "0 hours remaining"
            hoursRemainingNumber: hoursRemaining || 1,
            percentNumber: formattedIncentivePercent,
          }}
        />
      );
    }
    if (isMaximalistFoi) {
      return (
        <StrictLocalMsg
          defaultMessage="{hoursRemainingNumber, plural, one{<MediumFormatting># hour left</MediumFormatting> to get {percentNumber} off and free shipping on your first order.} other{<MediumFormatting># hours left</MediumFormatting> to get {percentNumber} off and free shipping on your first order.}}"
          description={{
            text: "Emphasize that there are only few days left for our promotion",
          }}
          values={{
            MediumFormatting,
            // Round up to 1 so we don't show "0 hours remaining"
            hoursRemainingNumber: hoursRemaining || 1,
            percentNumber: formattedIncentivePercent,
          }}
        />
      );
    }

    return (
      <StrictLocalMsg
        defaultMessage="{hoursRemainingNumber, plural, one{<MediumFormatting># hour left</MediumFormatting> to get {percentNumber} off your first order.} other{<MediumFormatting># hours left</MediumFormatting> to get {percentNumber} off your first order.}}"
        description={{
          text: "Emphasize that there are only few days left for our promotion",
        }}
        values={{
          MediumFormatting,
          // Round up to 1 so we don't show "0 hours remaining"
          hoursRemainingNumber: hoursRemaining || 1,
          percentNumber: formattedIncentivePercent,
        }}
      />
    );
  };

  const defaultAutoExtendText = () => {
    if (isFirstOrderForFaireDirectIncentive) {
      return (
        <StrictLocalMsg
          defaultMessage="{daysRemainingNumber, plural, one{<MediumFormatting># day left</MediumFormatting> to get {percentNumber} off your first order with any brand.} other{<MediumFormatting># days left</MediumFormatting> to get {percentNumber} off your first order with any brand.}}"
          description={{
            text: "Emphasize that there are only few days left for our promotion",
          }}
          values={{
            MediumFormatting,
            daysRemainingNumber,
            percentNumber: formattedIncentivePercent,
          }}
        />
      );
    }
    if (isMaximalistFoi) {
      return (
        <StrictLocalMsg
          defaultMessage="{daysRemainingNumber, plural, one{<MediumFormatting># day left</MediumFormatting> to get {percentNumber} off and free shipping on your first order.} other{<MediumFormatting># days left</MediumFormatting> to get {percentNumber} off and free shipping on your first order.}}"
          description={{
            text: "Emphasize that there are only few days left for our promotion",
          }}
          values={{
            MediumFormatting,
            daysRemainingNumber,
            percentNumber: formattedIncentivePercent,
          }}
        />
      );
    }

    return (
      <StrictLocalMsg
        defaultMessage="{daysRemainingNumber, plural, one{<MediumFormatting># day left</MediumFormatting> to get {percentNumber} off your first order.} other{<MediumFormatting># days left</MediumFormatting> to get {percentNumber} off your first order.}}"
        description={{
          text: "Emphasize that there are only few days left for our promotion",
        }}
        values={{
          MediumFormatting,
          daysRemainingNumber,
          percentNumber: formattedIncentivePercent,
        }}
      />
    );
  };

  const autoExtendText = () => {
    switch (true) {
      case hoursRemaining < 24:
        return autoExtendLessThan24HoursText();
      default:
        return defaultAutoExtendText();
    }
  };

  return autoExtendText();
};
